import type { ComponentProps } from "react"

/** Everything a `<textarea>` takes, plus its label, hint and error. */
export type TextAreaFieldProps = ComponentProps<"textarea"> &
  Readonly<{
    label: string
    hint?: string | undefined
    error?: string | undefined
  }>

/** A labeled text area, with its hint and error tied to it for a screen reader. */
export const TextAreaField = ({ label, hint, error, id, className = "", ...rest }: TextAreaFieldProps) => {
  const fieldId = id ?? rest.name ?? label.toLowerCase().replace(/\s+/g, "-")
  const describedBy = [hint === undefined ? undefined : `${fieldId}-hint`, error === undefined ? undefined : `${fieldId}-error`].filter(
    (value): value is string => value !== undefined,
  )

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-ink" htmlFor={fieldId}>
        {label}
      </label>
      <textarea
        rows={5}
        {...rest}
        className={[
          "w-full rounded-lg border bg-surface px-3 py-2.5 text-sm text-ink placeholder:text-ink-faint transition-colors",
          error === undefined ? "border-edge hover:border-ink-faint" : "border-danger",
          className,
        ].join(" ")}
        id={fieldId}
        aria-invalid={error !== undefined}
        aria-describedby={describedBy.length === 0 ? undefined : describedBy.join(" ")}
      />
      {hint === undefined ? null : (
        <p className="text-sm text-ink-faint" id={`${fieldId}-hint`}>
          {hint}
        </p>
      )}
      {error === undefined ? null : (
        <p className="text-sm text-danger" id={`${fieldId}-error`} role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
